import React, { useEffect } from "react";
import { makeStyles, Tooltip, Dropdown, Option, mergeClasses } from "@fluentui/react-components";
import { getWrapupConfiguration } from "../../utils/callCenterSettings";

const useStyles = makeStyles({
  callResultWrapper: {
    width: "98%",
    margin: "0% 1% 1% 1%",
  },

  callResultRow: {
    display: "flex",
    gap: "8px",
    width: "98%",
    margin: "0% 1% 1% 1%", //"0% 1% 2% 1%",
    alignItems: "flex-start",
    overflowX: "hidden",
  },

  col40: {
    width: "40%",
    minWidth: 0,
  },

  col60: {
    width: "60%",
    minWidth: 0,
  },

  col100: {
    width: "100%",
    minWidth: 0,
  },

  dropdownTruncate: {
    width: "100%",
    minWidth: 0,
    display: "block",

    /* target the trigger button rendered by Fluent */
    "& .fui-Dropdown__button": {
      width: "100% !important",
      minWidth: 0,
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      boxSizing: "border-box",
      //paddingRight: 8,
      //paddingLeft: 8,
      padding: "2px 5px",
      height: "28px", //32, /* fixed single-line height to prevent wrapping */
      //lineHeight: "32px",
      overflowX: "hidden" /* horizontal overflow hidden so ellipsis works */,
      overflowY: "hidden" /* ensure no vertical scrollbar on closed trigger */,
      whiteSpace: "nowrap",
      textOverflow: "ellipsis",
      scrollbarWidth: "none",
      msOverflowStyle: "none",
    },

    /* ensure the text portion is clipped with ellipsis */
    "& .fui-Dropdown__button, & .fui-Dropdown__button > span, & .fui-Dropdown__button > span > span":
      {
        overflow: "hidden",
        textOverflow: "ellipsis",
        whiteSpace: "nowrap",
        display: "inline-block",
        maxWidth: "100%",
        verticalAlign: "middle",
      },

    /* keep the caret icon from causing wrapping */
    "& .fui-Dropdown__expandIcon": {
      flex: "0 0 auto",
      marginLeft: 8,
      display: "inline-flex",
      alignItems: "center",
      justifyContent: "center",
    },

    /* hide nested clear button(s) inside the trigger (safe: only nested buttons) */
    "& .fui-Dropdown__button button": {
      display: "none !important",
      width: 0,
      height: 0,
      padding: 0,
      margin: 0,
      border: "none",
    },

    /* remove native scrollbars on the trigger in WebKit and Firefox */
    "& .fui-Dropdown__button::-webkit-scrollbar": {
      display: "none",
      width: 0,
      height: 0,
    },
  },

  /* option text wrapper used inside Option so open menu items can wrap */
  optionWrap: {
    display: "inline-block",
    whiteSpace: "normal" /* allow wrapping in the open menu */,
    wordBreak: "break-word",
    maxWidth: "100%",
  },

  /* popover helper: ensure popover content uses box-sizing and doesn't overflow trigger width */
  popoverMatch: {
    boxSizing: "border-box",
    minWidth: "100%",
    width: "100%",
    maxHeight: 320,
    overflowY: "auto",
  },
});

export const CallResult = (props) => {
  const classes = useStyles();
  const groups = (Array.isArray(getWrapupConfiguration()) ? getWrapupConfiguration() : []).filter(
    (group) => group && Array.isArray(group.values) && group.values.length > 0,
  );
  const groupOptions = groups
    .map((group) => group?.name || "")
    .filter((name) => name.trim().length > 0);
  const selectedGroupEntry =
    groups.find((group) => group?.name === props.callResultGroup) || groups[0] || null;
  const configuredGroupDefault = (selectedGroupEntry?.defaultValue || "").trim();
  const hasConfiguredDefault =
    Array.isArray(selectedGroupEntry?.values) &&
    selectedGroupEntry.values.includes(configuredGroupDefault);
  const valueOptions = Array.isArray(selectedGroupEntry?.values)
    ? hasConfiguredDefault
      ? [...selectedGroupEntry.values]
      : ["--None--", ...selectedGroupEntry.values]
    : [];

  const showWrapupValOptions = valueOptions.length > 0;
  const showWrapupGrpOptions = groupOptions.length > 1;

  const resolvedGroupValue =
    props.callResultGroup && props.callResultGroup.trim().length > 0
      ? props.callResultGroup
      : groupOptions.length > 0
        ? groupOptions[0]
        : "";

  const resolvedGroupSelectedOptions = resolvedGroupValue ? [resolvedGroupValue] : [];

  //ensure grp is initialized
  useEffect(() => {
    if (props.isDisabled) return;

    if (groupOptions.length > 0) {
      // If no group is set yet, initialize it
      if (!props.callResultGroup || props.callResultGroup.trim().length === 0) {
        props.onChangeCallResultGroup(groupOptions[0]);
      }
    }
  }, [groupOptions, props.isDisabled, props.callResultGroup]);

  useEffect(() => {
    if (props.isDisabled) return;

    if (selectedGroupEntry && Array.isArray(selectedGroupEntry.values)) {
      const newValues = selectedGroupEntry.values;

      const configuredGroupDefault = (selectedGroupEntry?.defaultValue || "").trim();

      const defaultValueForGroup = newValues.includes(configuredGroupDefault)
        ? configuredGroupDefault
        : "--None--";

      // Also applies when the config has values/defaults but no visible group name.
      if (!props.callResult || !newValues.includes(props.callResult)) {
        props.onChangeCallResult(defaultValueForGroup);
      }
    }
  }, [props.callResultGroup, props.callResult, selectedGroupEntry, props.isDisabled]);

  useEffect(() => {
    props.onChangeShowWrapup(Boolean(showWrapupValOptions));
  }, [showWrapupValOptions, props.onChangeShowWrapup]);

  if (!showWrapupValOptions) {
    return null;
  }

  const groupTooltipContent = resolvedGroupValue ? resolvedGroupValue : "Call Result Group";

  const valueTooltipContent =
    props.callResult && props.callResult.trim().length > 0
      ? props.callResult
      : "Select a Call Result";

  return (
    <>
      <div className={classes.callResultRow}>
        {showWrapupGrpOptions && (
          <div className={classes.col40}>
            <Tooltip content={groupTooltipContent} relationship="label">
              <Dropdown
                className={mergeClasses(classes.callResultWrapper, classes.dropdownTruncate)}
                placeholder="Select a Call Result Group"
                value={resolvedGroupValue}
                selectedOptions={resolvedGroupSelectedOptions}
                disabled={!!props.isDisabled}
                onOptionSelect={(event, data) => {
                  props.onChangeCallResultGroup(data.optionValue);
                }}
                menuPopover={{ style: classes.popoverMatch }}
              >
                {groupOptions.map((opt) => (
                  <Option key={opt} value={opt}>
                    <span className={classes.optionWrap}>{opt}</span>
                  </Option>
                ))}
              </Dropdown>
            </Tooltip>
          </div>
        )}
        <div className={showWrapupGrpOptions ? classes.col60 : classes.col100}>
          <Tooltip content={valueTooltipContent} relationship="label">
            <Dropdown
              className={mergeClasses(classes.callResultWrapper, classes.dropdownTruncate)}
              placeholder="Select a Call Result"
              value={props.callResult || ""}
              selectedOptions={props.callResult ? [props.callResult] : []}
              disabled={!!props.isDisabled}
              onOptionSelect={(event, data) => {
                const newValue = data.optionValue === "--None--" ? "" : data.optionValue;
                props.onChangeCallResult(newValue);
              }}
              menuPopover={{ style: classes.popoverMatch }}
            >
              {valueOptions.map((opt) => (
                <Option key={opt} value={opt}>
                  <span className={classes.optionWrap}>{opt}</span>
                </Option>
              ))}
            </Dropdown>
          </Tooltip>
        </div>
      </div>
    </>
  );
};
